<template>
    <tr>
        <td class="t_center"><label class="label label-inverse">{{ item.number }}</label></td>
        <td class="t_center">
            {{ item.issue_date_at | pmlDate('DD/MM/YYYY') }}
            <small class="show m-t-5" v-if="item.due_date_at">
                <i class="fa fa-exclamation-triangle text-warning" v-if="isExpired()"></i>
                {{ item.due_date_at | pmlDate('DD/MM/YYYY') }}
            </small>
        </td>
        <td class="t_right">
            <p class="m-0" v-if="tenant.currency_id !== item.currency_id">
                <small :class="getExchangeClass()" class="pull-left" v-pml-tooltip="$t('compared_to_today')">
                    <i class="fa" :class="{ 'fa-arrow-up': diffExchangeRate > 1, 'fa-arrows-h': diffExchangeRate == 1, 'fa-arrow-down': diffExchangeRate < 1 }"></i>
                </small>
                {{ exchangeRate | pmlFormatCurrencyById(tenant.currency_id) }}
            </p>
        </td>
        <td class="t_center"><label class="label" :class="getStatusLabel(item.status)">{{ $t(item.status) }}</label></td>
        <td class="t_right">
            {{ item.total_products + item.total_discounts + item.total_taxes | pmlFormatCurrencyById(tenant.currency_id) }}
            <small class="show" v-if="tenant.currency_id !== item.currency_id">
                ({{ (item.total_products + item.total_discounts + item.total_taxes) * item.exchange_rate | pmlFormatCurrencyById(item.currency_id) }})
            </small>
        </td>
        <td class="t_center">
            <span class="btn btn-info btn-xs" v-if="item.auto">auto</span>
            <span class="btn btn-warning btn-xs" v-pml-tooltip="$t('reopen')" v-if="canBeReopened() && deal.editable" @click.prevent="changeStatus('sent')"><i class="fa-fw fa fa-mail-reply"></i></span>
            <a :href="`/my-quotation/${item.code}`" target="_blank" class="btn btn-primary btn-xs" v-pml-tooltip="$t('show')"><i class="fa-fw md md-visibility"></i></a>
            <span class="btn btn-inverse btn-xs" v-pml-tooltip="$t('duplicate')" @click.prevent="clone" v-pml-block="isCloning" v-if="deal.editable && deal.status === 'open' && !item.auto"><i class="fa-fw fa fa-files-o"></i></span>
            <span class="btn btn-default btn-xs" @click.prevent="changeStatus('sent')" v-pml-tooltip="$t('mark_as_sent')" v-pml-block="isMarkingAsSent" v-if="canMarkAsSent() && deal.editable && !item.auto"><i class="fa-fw fa fa-share"></i></span>
            <router-link :to="{ name: 'deals.show.quotations.send', params: { id: item.deal_id, quotation: item.id } }" class="btn btn-default btn-xs" v-pml-tooltip="$t('send')" v-if="canBeSent() && deal.editable && !item.auto"><i class="fa-fw fa fa-share"></i></router-link>
            <router-link :to="{ name: 'deals.show.quotations.edit', params: { id: item.deal_id, quotation: item.id } }" class="btn btn-warning btn-xs" v-pml-tooltip="$t('edit')" v-if="canBeEdited() && deal.editable && !item.auto"><i class="fa-fw fa fa-pencil"></i></router-link>
            <span class="btn btn-danger btn-xs" v-pml-tooltip="$t('delete')" v-pml-block="isDeleting" @click.prevent="askToDelete()" v-if="canBeRemoved() && deal.editable"><i class="fa-fw fa fa-times"></i></span>
        </td>
    </tr>
</template>

<script>
    import Notify from 'utils/helpers/notify'
    import moment from 'moment'
    import { trans } from 'utils/helpers/translation'
    import { quotationStatus } from 'utils/helpers/label-colorify'
    import { findCurrency } from 'utils/helpers/find-store'
    import { mapGetters } from 'vuex'
    import { destroy, changeStatus, clone } from 'services/quotations'
    import { isNil, round } from 'lodash'
    import { askToDelete } from 'utils/helpers/sweet-alert'

    export default {
        name: 'ItemQuotation',
        props: [ 'item' ],
        data () {
            return {
                isDeleting: false,
                isMarkingAsSent: false,
                isCloning: false
            }
        },
        computed: {
            ...mapGetters({
                'tenant': 'getTenant',
                'deal': 'getDealInfo',
                'lead': 'getLeadInfo',
                'tenantCurrency': 'getTenantCurrency'
            }),
            exchangeRate () {
                return round(1 / this.item.exchange_rate, 2).toFixed(2)
            },
            currentExchangeRate () {
                return round(1 / this.getCurrency(this.item.currency_id).pivot.exchange_rate, 2).toFixed(2)
            },
            diffExchangeRate () {
                return (this.item.exchange_rate / this.getCurrency(this.item.currency_id).pivot.exchange_rate).toFixed(2)
            }
        },
        methods: {
            getStatusLabel: (status) => quotationStatus(status),
            getCurrency: (id) => findCurrency(id),
            getExchangeClass () {
                if (this.diffExchangeRate === '1.00')
                    return 'text-primary'

                return this.diffExchangeRate > 1 ? 'text-success' : 'text-danger'
            },
            askToDelete () {
                askToDelete()
                    .then((response) => (response.value) ? this.destroy() : null)
            },
            destroy () {
                this.isDeleting = true

                destroy(this.item.deal_id, this.item.id)
                    .then(() => Notify.success(trans('_notifications.success')))
                    .catch(() => Notify.error(trans('_notifications.error')))
                    .then(() => { this.isDeleting = false })
            },
            changeStatus (newStatus) {
                this.isMarkingAsSent = true
                changeStatus(this.item.deal_id, this.item.id, newStatus)
                    .then(() => Notify.success(trans('_notifications.success')))
                    .catch(() => Notify.error(trans('_notifications.error')))
                    .then(() => { this.isMarkingAsSent = false })
            },
            clone () {
                this.isCloning = true

                clone(this.item.deal_id, this.item.id)
                    .then(() => Notify.success(trans('_notifications.success')))
                    .catch(() => Notify.error(trans('_notifications.error')))
                    .then(() => { this.isCloning = false })
            },
            isExpired () {
                return (this.item.status === 'new' || this.item.status === 'sent') &&
                    (this.item.due_date_at < moment().format('YYYY-MM-DD'))
            },
            canBeRemoved () {
                return this.item.status === 'new' && this.deal.status === 'open'
            },
            canBeEdited () {
                return this.item.status === 'new' && this.deal.status === 'open'
            },
            canBeReopened () {
                return (this.item.status === 'approved' || this.item.status === 'refused') && this.deal.status === 'open'
            },
            canBeSent () {
                return (this.item.status !== 'approved' && this.item.status !== 'refused' && !isNil(this.lead.email) && this.lead.email !== '') && this.deal.status === 'open'
            },
            canMarkAsSent () {
                return (this.item.status !== 'approved' && this.item.status !== 'sent' && this.item.status !== 'refused' && !this.lead.email) && this.deal.status === 'open'
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/modules/deals/components/show/quotations/Item.vue